"use client";

import { useEffect, useState } from 'react';

export type AudienceMode = 'private' | 'company';

export interface AudienceToggleProps {
	value: AudienceMode;
	onChange: (mode: AudienceMode) => void;
	className?: string;
}

const STORAGE_KEY = 'karrelo-homepage-audience';

export function AudienceToggle({ value, onChange, className = '' }: AudienceToggleProps) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!mounted) return;
		try {
			sessionStorage.setItem(STORAGE_KEY, value);
		} catch {
			// Ignore storage errors
		}
	}, [value, mounted]);

	if (!mounted) {
		return (
			<div
				className={`flex rounded-full bg-slate-200 dark:bg-slate-700 p-1 gap-1 ${className}`}
				aria-hidden="true"
			>
				<span className="px-3 py-1.5 text-sm font-medium rounded-full text-slate-500">Privat</span>
				<span className="px-3 py-1.5 text-sm font-medium rounded-full text-slate-500">Företag</span>
			</div>
		);
	}

	return (
		<div
			role="tablist"
			aria-label="Välj vy: privat eller företag"
			className={`flex rounded-full bg-slate-200 dark:bg-slate-700 p-1 gap-1 ${className}`}
		>
			<button
				role="tab"
				aria-selected={value === 'private'}
				aria-label="Visa innehåll för jobbsökare"
				onClick={() => onChange('private')}
				onKeyDown={(e) => {
					if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
						e.preventDefault();
						onChange('company');
					}
				}}
				className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
					value === 'private'
						? 'bg-white dark:bg-slate-800 text-primary-600 dark:text-primary-400 shadow-sm'
						: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
				}`}
			>
				Privat
			</button>
			<button
				role="tab"
				aria-selected={value === 'company'}
				aria-label="Visa innehåll för företag"
				onClick={() => onChange('company')}
				onKeyDown={(e) => {
					if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
						e.preventDefault();
						onChange('private');
					}
				}}
				className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
					value === 'company'
						? 'bg-white dark:bg-slate-800 text-primary-600 dark:text-primary-400 shadow-sm'
						: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
				}`}
			>
				Företag
			</button>
		</div>
	);
}

export function getStoredAudience(): AudienceMode | null {
	if (typeof window === 'undefined') return null;
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (stored === 'private' || stored === 'company') return stored;
		return null;
	} catch {
		return null;
	}
}
